import {
    Globe, Link2, Github, Gitlab, GitBranch, Figma, Slack, Chrome, Twitter, Youtube,
    Linkedin, Trello, Framer, Codepen, Codesandbox, Dribbble, Twitch,
    Cloud, Flame, Sparkles, Database, Server, Terminal, Container, Package,
    Book, BookOpen, Bug, Box, Boxes, Zap, Shield, CreditCard, Mail, Calendar,
    Users, Play, Monitor, BarChart3, Palette, FileText, LayoutDashboard,
    Bot, Video, ShoppingCart, Map, Camera, PenTool, Wrench, Key, Lock,
    Activity, Send, MessageSquare, Layers, Cpu, HardDrive, Network,
} from 'lucide-react';

/**
 * The project-links vocabulary — one registry shared by the console's top bar
 * and the settings editor, so a link is drawn identically wherever it appears.
 *
 * Icons are lucide components referenced by a short string key, which is what
 * gets persisted. Two reasons it is a key and not a URL: the previous version
 * hotlinked logo PNGs from ai-bot.cn, vectorseek.com and logos-world.net, so
 * three unrelated sites had to stay up for the bar to render; and a key is a
 * stable identifier that survives an icon set being restyled.
 *
 * A link is `{ title, url, type, icon }`. `type` is `custom` for anything the
 * user adds; the four legacy types keep working without an `icon` field, which
 * is why resolveLinkIcon falls back through type before guessing.
 */

// ── Icon registry ────────────────────────────────────────────────────────────
// Grouped only for the picker's benefit; lookup is flat.
export const ICON_GROUPS = [
    {
        label: 'Code',
        icons: {
            github: { icon: Github, label: 'GitHub' },
            gitlab: { icon: Gitlab, label: 'GitLab' },
            branch: { icon: GitBranch, label: 'Branch' },
            terminal: { icon: Terminal, label: 'Terminal' },
            package: { icon: Package, label: 'Package' },
            container: { icon: Container, label: 'Container' },
            codesandbox: { icon: Codesandbox, label: 'CodeSandbox' },
            codepen: { icon: Codepen, label: 'CodePen' },
        },
    },
    {
        label: 'Infrastructure',
        icons: {
            cloud: { icon: Cloud, label: 'Cloud' },
            firebase: { icon: Flame, label: 'Firebase' },
            server: { icon: Server, label: 'Server' },
            database: { icon: Database, label: 'Database' },
            cpu: { icon: Cpu, label: 'Compute' },
            drive: { icon: HardDrive, label: 'Storage' },
            network: { icon: Network, label: 'Network' },
            shield: { icon: Shield, label: 'Security' },
        },
    },
    {
        label: 'Design',
        icons: {
            figma: { icon: Figma, label: 'Figma' },
            framer: { icon: Framer, label: 'Framer' },
            dribbble: { icon: Dribbble, label: 'Dribbble' },
            palette: { icon: Palette, label: 'Palette' },
            pen: { icon: PenTool, label: 'Pen' },
            camera: { icon: Camera, label: 'Camera' },
            layers: { icon: Layers, label: 'Layers' },
            monitor: { icon: Monitor, label: 'Monitor' },
        },
    },
    {
        label: 'Team',
        icons: {
            slack: { icon: Slack, label: 'Slack' },
            message: { icon: MessageSquare, label: 'Chat' },
            mail: { icon: Mail, label: 'Mail' },
            users: { icon: Users, label: 'People' },
            calendar: { icon: Calendar, label: 'Calendar' },
            trello: { icon: Trello, label: 'Board' },
            linkedin: { icon: Linkedin, label: 'LinkedIn' },
            send: { icon: Send, label: 'Send' },
        },
    },
    {
        label: 'Content',
        icons: {
            book: { icon: Book, label: 'Book' },
            docs: { icon: BookOpen, label: 'Docs' },
            file: { icon: FileText, label: 'Document' },
            youtube: { icon: Youtube, label: 'YouTube' },
            video: { icon: Video, label: 'Video' },
            twitch: { icon: Twitch, label: 'Twitch' },
            twitter: { icon: Twitter, label: 'X' },
            play: { icon: Play, label: 'Play' },
        },
    },
    {
        label: 'Other',
        icons: {
            globe: { icon: Globe, label: 'Website' },
            link: { icon: Link2, label: 'Link' },
            chrome: { icon: Chrome, label: 'Browser' },
            ai: { icon: Sparkles, label: 'AI' },
            bot: { icon: Bot, label: 'Bot' },
            dashboard: { icon: LayoutDashboard, label: 'Dashboard' },
            analytics: { icon: BarChart3, label: 'Analytics' },
            activity: { icon: Activity, label: 'Monitoring' },
            bug: { icon: Bug, label: 'Issues' },
            payments: { icon: CreditCard, label: 'Payments' },
            store: { icon: ShoppingCart, label: 'Store' },
            map: { icon: Map, label: 'Map' },
            tools: { icon: Wrench, label: 'Tools' },
            key: { icon: Key, label: 'Keys' },
            lock: { icon: Lock, label: 'Vault' },
            zap: { icon: Zap, label: 'Automation' },
            box: { icon: Box, label: 'Box' },
            boxes: { icon: Boxes, label: 'Bundle' },
        },
    },
];

const ICON_REGISTRY = Object.fromEntries(
    ICON_GROUPS.flatMap((group) => Object.entries(group.icons))
);

export const DEFAULT_ICON_KEY = 'link';

export function iconComponent(key) {
    return ICON_REGISTRY[key]?.icon ?? Link2;
}

export function iconLabel(key) {
    return ICON_REGISTRY[key]?.label ?? 'Link';
}

// ── Legacy types ─────────────────────────────────────────────────────────────
// Written by onboarding before links were user-editable. They carry no `icon`,
// so the type is what resolves them.
const TYPE_ICONS = {
    'ai-studio': 'ai',
    firebase: 'firebase',
    gcp: 'cloud',
    github: 'github',
};

/** Offered as one-tap adds in the editor when not already present. */
export const LINK_PRESETS = [
    { type: 'ai-studio', title: 'Google AI Studio', icon: 'ai' },
    { type: 'firebase', title: 'Firebase Console', icon: 'firebase' },
    { type: 'gcp', title: 'Google Cloud', icon: 'cloud' },
    { type: 'github', title: 'GitHub Repository', icon: 'github' },
];

// ── URL helpers ──────────────────────────────────────────────────────────────

/**
 * Users type `figma.com/file/…`, not `https://figma.com/file/…`. Without a
 * scheme, window.open resolves the value against the app's own origin and lands
 * on a 404 inside Forge instead of leaving for the target site.
 *
 * Non-http schemes are left alone so `mailto:` and app deep links survive.
 */
export function normalizeUrl(raw) {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return '';
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

export function hostOf(url) {
    try {
        return new URL(normalizeUrl(url)).hostname.replace(/^www\./, '');
    } catch {
        return '';
    }
}

export function isValidUrl(url) {
    const normalized = normalizeUrl(url);
    if (!normalized) return false;
    try {
        const parsed = new URL(normalized);
        // A bare `https://` parses fine but points nowhere.
        return !!parsed.hostname || parsed.protocol === 'mailto:';
    } catch {
        return false;
    }
}

// Ordered: the first hint whose fragment appears in the hostname wins, so more
// specific hosts must come before the domains that contain them.
const HOST_HINTS = [
    ['aistudio.google', 'ai'],
    ['makersuite.google', 'ai'],
    ['console.firebase', 'firebase'],
    ['console.cloud.google', 'cloud'],
    ['github', 'github'],
    ['gitlab', 'gitlab'],
    ['figma', 'figma'],
    ['framer', 'framer'],
    ['dribbble', 'dribbble'],
    ['slack', 'slack'],
    ['discord', 'message'],
    ['linkedin', 'linkedin'],
    ['youtube', 'youtube'],
    ['youtu.be', 'youtube'],
    ['twitch', 'twitch'],
    ['notion', 'docs'],
    ['linear.app', 'dashboard'],
    ['trello', 'trello'],
    ['sentry', 'bug'],
    ['posthog', 'analytics'],
    ['analytics.google', 'analytics'],
    ['stripe', 'payments'],
    ['supabase', 'database'],
    ['planetscale', 'database'],
    ['neon.tech', 'database'],
    ['mongodb', 'database'],
    ['vercel', 'zap'],
    ['netlify', 'zap'],
    ['railway', 'server'],
    ['render.com', 'server'],
    ['fly.io', 'server'],
    ['aws.amazon', 'cloud'],
    ['azure', 'cloud'],
    ['docker', 'container'],
    ['npmjs', 'package'],
    ['codesandbox', 'codesandbox'],
    ['codepen', 'codepen'],
    ['x.com', 'twitter'],
    ['twitter', 'twitter'],
];

/** Best guess from a URL, so adding a link rarely needs the picker at all. */
export function guessIconKey(url) {
    const host = hostOf(url);
    if (!host) return DEFAULT_ICON_KEY;
    const hit = HOST_HINTS.find(([fragment]) => host.includes(fragment));
    return hit ? hit[1] : 'globe';
}

/**
 * The icon a link should render with. Explicit choice first, then the legacy
 * type, then the hostname — so links saved before any of this existed still
 * come out looking right.
 */
export function resolveLinkIcon(link) {
    if (link?.icon && ICON_REGISTRY[link.icon]) return ICON_REGISTRY[link.icon].icon;
    if (link?.type && TYPE_ICONS[link.type]) return iconComponent(TYPE_ICONS[link.type]);
    return iconComponent(guessIconKey(link?.url));
}

/** What to call a link that has a URL but no title yet. */
export function linkLabel(link) {
    return link?.title?.trim() || hostOf(link?.url) || 'Untitled link';
}

// ── Editor rows ──────────────────────────────────────────────────────────────
// Persisted links have no id, but drag-to-reorder needs a stable key that
// survives reordering — an array index does not. Rows carry a client-side uid
// that is stripped before the array is sent back.

let seq = 0;
const nextUid = () => `link-${(seq += 1)}`;

export function toRows(links = []) {
    return links.map((link) => ({ ...link, uid: nextUid() }));
}

/** Drops incomplete rows and normalises what survives. */
export function fromRows(rows = []) {
    return rows
        .filter((row) => row.url?.trim())
        .map((row) => ({
            title: row.title?.trim() || hostOf(row.url) || 'Link',
            url: normalizeUrl(row.url),
            type: row.type || 'custom',
            icon: row.icon || guessIconKey(row.url),
        }));
}

export function blankRow(preset) {
    return {
        uid: nextUid(),
        title: preset?.title ?? '',
        url: '',
        type: preset?.type ?? 'custom',
        icon: preset?.icon ?? DEFAULT_ICON_KEY,
    };
}
